import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import type { AdminApplication, ApplicationStatus } from '@/api/models';

interface ApplicationEditDialogProps {
  open: boolean;
  application: AdminApplication;
  onClose: () => void;
  onSaved: (application: AdminApplication) => void;
}

const STATUS_VALUES: ApplicationStatus[] = ['active', 'suspended'];

/**
 * `PATCH /v1/admin/applications/{code}` — every field optional, only the
 * fields present are changed; `code` never changes here. `notifySecret` is
 * write-only and always starts blank: leaving it blank leaves the existing
 * secret untouched (there is nothing to prefill it with — the API never
 * returns one).
 */
export function ApplicationEditDialog({ open, application, onClose, onSaved }: ApplicationEditDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [name, setName] = useState(application.name);
  const [status, setStatus] = useState<ApplicationStatus>(application.status);
  const [notifyUrl, setNotifyUrl] = useState(application.notifyUrl ?? '');
  const [notifySecret, setNotifySecret] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(application.name);
      setStatus(application.status);
      setNotifyUrl(application.notifyUrl ?? '');
      setNotifySecret('');
      setAccessDenied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateAdminApplication(application.code, {
        name: name.trim(),
        status,
        notifyUrl: notifyUrl.trim() || undefined,
        notifySecret: notifySecret.trim() || undefined,
      });
      notify(translate('pay.applications.edit.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.applications.edit.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label={translate('pay.applications.edit.fieldName')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
            <TextField
              select
              size="small"
              label={translate('pay.applications.edit.fieldStatus')}
              value={status}
              onChange={(event) => setStatus(event.target.value as ApplicationStatus)}
              fullWidth
            >
              {STATUS_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(`pay.applications.status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={translate('pay.applications.edit.fieldNotifyUrl')}
              value={notifyUrl}
              onChange={(event) => setNotifyUrl(event.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              type="password"
              autoComplete="off"
              label={translate('pay.applications.edit.fieldNotifySecret')}
              helperText={translate('pay.applications.edit.fieldNotifySecretHelp')}
              value={notifySecret}
              onChange={(event) => setNotifySecret(event.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.applications.edit.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={name.trim().length === 0 || submitting}>
            {translate('pay.applications.edit.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
